import { ENGLISH_TEST_TYPE_VAL_LABEL_MAP } from 'data';
import { DocAccessType, DocumentMap, IDocumentsList } from '../../../../types';
import { Section } from '../../../Finger/Section';
import FingerWithApprove from '../../../atoms/finger-with-approve';
import NoDocuments from '../../../atoms/no-doc-placeholder';
import { getFingerStatus, getVisible, hasDeleted } from '../../../../utilities/documents';
import { UserRole } from '../../../../store/auth/types';

interface IProps {
  infoOnly: boolean;
  userAccess?: DocAccessType;
  documents: DocumentMap;
  englishTestType?: string;
  englishTestDone: boolean;
  englishTestNotReqdReason: string;
  warning?: string;
  userRole?: UserRole;
  canResume: boolean;
  canUpload: boolean;
  canOpen: boolean;
  canApprove: boolean;
  canReject: boolean;
  isDeleting: boolean;
  canAcknowledge: boolean;
  onResume: (url: string) => void;
  onUploadClick: (docKey: string) => void;
  onOpenClick: (docKey: string) => void;
  onApproveClick: (docKey: string) => void;
  onRejectClick: (docKey: string) => void;
  onRescindRejectClick: (docKey: string) => void;
  onDeleteClick: (docKey: string) => void;
  onAcknowledgeClick: (docKey: string) => void;
}

const getEnglishTestName = (englishTestType = '') => {
  const englishTestName = ENGLISH_TEST_TYPE_VAL_LABEL_MAP[englishTestType]
    ? ENGLISH_TEST_TYPE_VAL_LABEL_MAP[englishTestType]
    : englishTestType;
  return englishTestName.replace(/English Test/gi, '');
};

const EnglishTest = (props: IProps) => {
  const englishTests = props.documents ?? {};
  const { englishTestType, englishTestDone, infoOnly, userAccess, englishTestNotReqdReason, userRole } = props;
  let { warning } = props;
  let hasDocs = false;
  let hasDeletedDocs = false;

  const visibleDocs = getVisible({ documentsHash: englishTests, userAccess, infoOnly, userRole }) as IDocumentsList[];
  if (visibleDocs.length > 0) {
    hasDocs = true;
  }
  if (hasDeleted(englishTests)) {
    hasDeletedDocs = true;
  }

  const label = englishTestDone
    ? `You stated that you completed the ${getEnglishTestName(englishTestType)} English test`
    : 'You stated that you are going to complete an English test';

  warning =
    !warning && !englishTestDone ? 'Ensure this test is either an IELTS, TOEFL, Pearson or Cambridge test' : warning;

  if ((!hasDocs && !hasDeletedDocs) || !!englishTestNotReqdReason) {
    return null;
  }

  if ((infoOnly || userAccess) && !hasDocs) {
    return null;
  }

  return (
    <Section
      label={label}
      canEdit={props.canResume}
      edit={() => {
        props.onResume('personal-info/step-6');
      }}
      warning={warning}
    >
      {visibleDocs.length > 0 ? (
        visibleDocs.map((englishTest: IDocumentsList, index: number) => {
          const { key, item } = englishTest;
          return (
            <FingerWithApprove
              key={`english-test-${index}`}
              status={getFingerStatus(item)}
              isProcessingPdf={item.isProcessingPdf}
              reason={item.reason}
              fileCount={item.signedUrls && item.signedUrls.length > 0 ? item.signedUrls.length : 0}
              fileNames={item.fileNames}
              label={`English Test Results${
                Object.keys(englishTests).length > 1 ? (index > 0 ? ` ${index + 1}` : '') : ''
              }`}
              canUpload={infoOnly ? false : props.canUpload}
              canOpen={infoOnly ? false : props.canOpen}
              canApprove={infoOnly ? false : props.canApprove}
              canReject={infoOnly ? false : props.canReject}
              isDeleting={infoOnly ? false : props.isDeleting}
              canAcknowledge={infoOnly ? props.canAcknowledge : false}
              onUploadClick={() => props.onUploadClick(key)}
              onOpenClick={() => props.onOpenClick(key)}
              onApproveClick={() => props.onApproveClick(key)}
              onRejectClick={() => props.onRejectClick(key)}
              onRescindRejectClick={() => props.onRescindRejectClick(key)}
              onDeleteClick={() => props.onDeleteClick(key)}
              onAcknowledgeClick={() => props.onAcknowledgeClick(key)}
              userRole={props.userRole}
              deletedByTTL={item.deletedByTTL}
            />
          );
        })
      ) : (
        <NoDocuments />
      )}
    </Section>
  );
};

export default EnglishTest;
